import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, Lock, AlertTriangle } from "lucide-react";
import { jourCourt } from "@/lib/dates";
import { PLANNING_TYPES, fullName } from "./shared";

function startOfWeek(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() + ((day === 0 ? -6 : 1) - day));
  date.setHours(0, 0, 0, 0);
  return date;
}
const fmt = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const c = new Date(d); c.setDate(c.getDate() + n); return c; };

export function SitePlanningGrid({ fetchWeek, onSave, onDelete, canEdit, lockedHint }) {
  const [anchor, setAnchor] = useState(new Date());
  const [salaries, setSalaries] = useState([]);
  const [entries, setEntries] = useState([]);
  const [closed, setClosed] = useState([]);
  const [editing, setEditing] = useState(null);

  const { start, dates } = useMemo(() => {
    const s = startOfWeek(anchor);
    return { start: s, dates: Array.from({ length: 7 }, (_, i) => addDays(s, i)) };
  }, [anchor]);

  const load = async () => {
    const data = await fetchWeek(fmt(start), fmt(addDays(start, 6)));
    setSalaries(data.salaries || []);
    setEntries(data.entries || []);
    setClosed(data.closed_days || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start]);

  const entryAt = (sid, d) => entries.find((e) => e.salarie_id === sid && e.date === fmt(d));
  const isClosed = (d) => closed.includes(fmt(d));
  const conflicts = entries.filter((e) => e.type === "travaille" && closed.includes(e.date)).length;

  return (
    <div className="space-y-3" data-testid="site-planning">
      {conflicts > 0 && (
        <div className="flex items-center gap-2 text-xs font-semibold bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] px-3 py-2" data-testid="site-planning-closed-alert">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {conflicts} créneau{conflicts > 1 ? "x" : ""} travaillé{conflicts > 1 ? "s" : ""} planifié{conflicts > 1 ? "s" : ""} un jour où l'établissement est fermé (hors période d'ouverture).
        </div>
      )}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setAnchor(addDays(anchor, -7))} data-testid="site-planning-prev" className="h-9 px-2"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="h-9 px-3 border border-[#DEE2E6] bg-white flex items-center text-xs font-semibold" data-testid="site-planning-range">
            {start.toLocaleDateString("fr-FR")} → {addDays(start, 6).toLocaleDateString("fr-FR")}
          </div>
          <Button variant="outline" onClick={() => setAnchor(addDays(anchor, 7))} data-testid="site-planning-next" className="h-9 px-2"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        {!canEdit && lockedHint && (
          <div className="flex items-center gap-2 text-xs text-[#495057] bg-[#F8F9FA] border border-[#DEE2E6] px-3 py-1.5"><Lock className="h-3.5 w-3.5" /> {lockedHint}</div>
        )}
        <div className="flex gap-3 flex-wrap">
          {PLANNING_TYPES.map((t) => (
            <div key={t.key} className="flex items-center gap-1.5 text-[10px] font-semibold"><span className="h-2.5 w-2.5" style={{ background: t.color }} />{t.label}</div>
          ))}
        </div>
      </div>
      <div className="bg-white border border-[#DEE2E6] overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#F8F9FA] border-b border-[#DEE2E6]">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-semibold border-r border-[#DEE2E6] sticky left-0 bg-[#F8F9FA] min-w-[170px]">Salarié</th>
              {dates.map((d) => (
                <th key={fmt(d)} className={`px-2 py-2 text-center border-r border-[#DEE2E6] text-xs min-w-[68px] ${isClosed(d) ? "bg-[#FEF2F2] text-[#B91C1C]" : ""}`} data-testid={isClosed(d) ? `site-closed-day-${fmt(d)}` : undefined}>
                  <div className="uppercase tracking-wider font-semibold">{jourCourt(d)}</div>
                  <div className="font-mono">{d.getDate()}/{d.getMonth() + 1}</div>
                  {isClosed(d) && <div className="text-[9px] uppercase tracking-wider font-bold">Fermé</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {salaries.map((s) => (
              <tr key={s.id} className="border-b border-[#DEE2E6]">
                <td className="px-3 py-2 border-r border-[#DEE2E6] sticky left-0 bg-white font-semibold">
                  {fullName(s)}<div className="text-[11px] text-[#868E96] font-normal">{s.poste}</div>
                </td>
                {dates.map((d) => {
                  const e = entryAt(s.id, d);
                  const t = PLANNING_TYPES.find((x) => x.key === e?.type);
                  return (
                    <td key={fmt(d)} className={`border-r border-[#DEE2E6] p-0 text-center ${isClosed(d) ? "bg-[#FEF2F2]" : ""}`}>
                      <button disabled={!canEdit} onClick={() => setEditing({ salarie_id: s.id, date: fmt(d), current: e, closed: isClosed(d) })}
                              data-testid={`site-cell-${s.code}-${fmt(d)}`}
                              className="w-full h-12 hover:bg-[#F8F9FA] flex items-center justify-center transition-colors disabled:cursor-default relative"
                              style={t ? { backgroundColor: t.color, color: t.color === "#FFD700" ? "#0A0A0A" : "white" } : {}}>
                        {e ? <span className="text-[11px] font-semibold">{e.type === "travaille" ? (e.horaire_debut ? `${e.horaire_debut}-${e.horaire_fin}` : "T") : t?.label.charAt(0)}</span>
                           : <span className="text-[#DEE2E6]">{canEdit ? "+" : ""}</span>}
                        {e?.type === "travaille" && isClosed(d) && <AlertTriangle className="h-3 w-3 absolute top-0.5 right-0.5 text-[#FFD700]" data-testid={`site-cell-conflict-${s.code}-${fmt(d)}`} />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
            {salaries.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-sm text-[#868E96]">Aucun salarié affecté.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && (
        <EditModal editing={editing} onClose={() => setEditing(null)}
                   onSave={async (form) => { await onSave(form); setEditing(null); load(); }}
                   onDelete={editing.current && onDelete ? async () => { await onDelete(editing.current.id); setEditing(null); load(); } : null} />
      )}
    </div>
  );
}

function EditModal({ editing, onClose, onSave, onDelete }) {
  const [type, setType] = useState(editing.current?.type || "travaille");
  const [debut, setDebut] = useState(editing.current?.horaire_debut || "09:00");
  const [fin, setFin] = useState(editing.current?.horaire_fin || "17:00");
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white border border-[#0A0A0A] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="site-planning-modal">
        <h3 className="font-display text-xl font-black tracking-tight mb-4">Journée du {editing.date}</h3>
        {editing.closed && (
          <div className="flex items-center gap-2 text-xs font-semibold bg-[#FEF2F2] border border-[#FECACA] text-[#B91C1C] px-3 py-2 mb-4" data-testid="site-edit-closed-warning">
            <AlertTriangle className="h-4 w-4 shrink-0" /> L'établissement est fermé ce jour (hors période d'ouverture). Un créneau travaillé sera signalé.
          </div>
        )}
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Type</div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="site-edit-type" className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{PLANNING_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {type === "travaille" && (
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Début</div><Input type="time" value={debut} onChange={(e) => setDebut(e.target.value)} data-testid="site-edit-debut" className="h-11" /></div>
              <div><div className="text-xs uppercase tracking-[0.2em] font-semibold mb-2">Fin</div><Input type="time" value={fin} onChange={(e) => setFin(e.target.value)} data-testid="site-edit-fin" className="h-11" /></div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            {onDelete && <Button variant="outline" onClick={onDelete} data-testid="site-edit-delete" className="border-[#E60000] text-[#E60000]">Effacer</Button>}
            <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
            <Button onClick={() => onSave({ salarie_id: editing.salarie_id, date: editing.date, type, horaire_debut: type === "travaille" ? debut : null, horaire_fin: type === "travaille" ? fin : null })}
                    data-testid="site-edit-save" className="flex-1 bg-[#002FA7] hover:bg-[#002175] text-white">Enregistrer</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
